import React, { useId } from 'react';

const SLIDE_REFERENCE_WIDTH = 960;
const SLIDE_REFERENCE_HEIGHT = 540;

export const SHADOW_SHAPES = ['ellipse', 'rectangle', 'rounded', 'circle'];

// Decorative soft shadow placed under objects (images, stickers, etc.).
// Drawn in an SVG whose viewBox matches the element's size at the reference
// slide resolution, so blur scales together with the slide. The shape is inset
// by the blur radius so the soft edge fades out inside the element box and is
// never clipped by the wrapper's overflow:hidden.
const ShadowElement = ({ element }) => {
  const filterId = `shadow-blur-${useId().replace(/:/g, '')}`;
  const widthPct = element?.size?.width ?? 0;
  const heightPct = element?.size?.height ?? 0;
  const W = Math.max((widthPct / 100) * SLIDE_REFERENCE_WIDTH, 2);
  const H = Math.max((heightPct / 100) * SLIDE_REFERENCE_HEIGHT, 2);

  const content = element?.content || {};
  const shape = SHADOW_SHAPES.includes(content.shape) ? content.shape : 'ellipse';
  const color = content.color || '#000000';
  const opacity = Math.min(1, Math.max(0, content.opacity ?? 0.35));
  const blur = Math.max(0, content.blur ?? 12);
  const cornerRadius = Math.max(0, content.cornerRadius ?? 24);

  const maxInset = Math.min(W, H) * 0.45;
  const inset = Math.min(blur * 2, maxInset);
  // Shrink blur together with inset when the box is too small for it.
  const stdDev = inset / 2;
  const innerW = Math.max(W - inset * 2, 1);
  const innerH = Math.max(H - inset * 2, 1);

  let shapeNode;
  if (shape === 'ellipse') {
    shapeNode = <ellipse cx={W / 2} cy={H / 2} rx={innerW / 2} ry={innerH / 2} />;
  } else if (shape === 'circle') {
    const r = Math.min(innerW, innerH) / 2;
    shapeNode = <circle cx={W / 2} cy={H / 2} r={r} />;
  } else {
    const r = shape === 'rounded' ? Math.min(cornerRadius, innerW / 2, innerH / 2) : 0;
    shapeNode = <rect x={inset} y={inset} width={innerW} height={innerH} rx={r} ry={r} />;
  }

  return (
    <svg
      width="100%"
      height="100%"
      viewBox={`0 0 ${W} ${H}`}
      preserveAspectRatio="none"
      style={{ display: 'block', pointerEvents: 'none', overflow: 'visible' }}
    >
      {stdDev > 0 && (
        <defs>
          <filter id={filterId} x="-50%" y="-50%" width="200%" height="200%">
            <feGaussianBlur stdDeviation={stdDev} />
          </filter>
        </defs>
      )}
      <g
        fill={color}
        fillOpacity={opacity}
        filter={stdDev > 0 ? `url(#${filterId})` : undefined}
      >
        {shapeNode}
      </g>
    </svg>
  );
};

export default ShadowElement;
